import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BorderProps,
  BorderRadiusProps,
  BoxShadowProps,
  DisplayProps,
  JustifyContentProps,
  OpacityProps,
  PositionProps,
  TopProps,
  ZIndexProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  borderRadius,
  boxShadow,
  display,
  justifyContent,
  opacity,
  position,
  space,
  themeGet,
  top,
  zIndex,
} from 'styled-system';

import type { BackgroundColorProps, SpaceProps } from 'common/theme/system-types';
import type {
  CursorProps,
  ThemeLeftProps,
  ThemeRightProps,
  TransformProps,
  TransitionProps,
} from 'common/theme/system-utilities';
import {
  cursor,
  themeLeft,
  themeRight,
  transform,
  transition,
} from 'common/theme/system-utilities';
import ArrowIcon from './components/ArrowIcon';

const ICON_SIZE = '20px';

export interface ImageCarouselNavigationProps {
  hasNext: boolean;
  hasPrev: boolean;
  onPrevious: () => void;
  onNext: () => void;
  arrowIconSize?: number;
  className?: string;
}

export const NavigationButton = styled.button<
  PositionProps &
    SpaceProps &
    AlignItemsProps &
    JustifyContentProps &
    ZIndexProps &
    BoxShadowProps &
    BackgroundColorProps &
    TopProps &
    BorderRadiusProps &
    BorderProps &
    DisplayProps &
    TransitionProps &
    OpacityProps &
    CursorProps
>`
  ${alignItems};
  ${backgroundColor};
  ${borderRadius};
  ${border};
  ${boxShadow};
  ${cursor};
  ${display};
  ${justifyContent};
  ${opacity};
  ${position};
  ${space};
  ${top};
  ${transition};
  ${zIndex};

  &:hover {
    background-color: ${themeGet('colors.sys.neutral.background.default')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const navigationButtonStyles: ComponentProps<typeof NavigationButton> = {
  alignItems: 'center',
  bg: 'sys.neutral.background.default',
  border: 'none',
  borderRadius: 'circle',
  boxShadow: 'high',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  opacity: 0.8,
  padding: 'sp_6',
  position: 'absolute',
  top: '50%',
  transition: 'background-color 200ms ease',
  zIndex: 1,
  tabIndex: -1,
};

const PreviousButton = styled(NavigationButton)<ThemeLeftProps & TransformProps>`
  ${themeLeft};
  ${transform};
`;

const previousButtonStyles: ComponentProps<typeof PreviousButton> = {
  ...navigationButtonStyles,
  themeLeft: 'sp_8',
  transform: 'translateY(-50%)',
};

const NextButton = styled(NavigationButton)<ThemeRightProps & TransformProps>`
  ${themeRight};
  ${transform};
`;

const nextButtonStyles: ComponentProps<typeof NextButton> = {
  ...navigationButtonStyles,
  themeRight: 'sp_8',
  transform: 'translateY(-50%) rotate(180deg)',
};

export const PrevControlButton = ({
  hasPrev,
  onPrevious,
  arrowIconSize,
  className,
}: Pick<
  ImageCarouselNavigationProps,
  'hasPrev' | 'onPrevious' | 'arrowIconSize' | 'className'
>) => (
  <PreviousButton
    {...previousButtonStyles}
    onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      onPrevious();
    }}
    aria-hidden={true}
    disabled={hasPrev}
    data-testid="carousel-prev-button"
    className={className}
  >
    <ArrowIcon arrowSize={arrowIconSize ?? ICON_SIZE} alt="" />
  </PreviousButton>
);

export const NextControlButton = ({
  hasNext,
  onNext,
  arrowIconSize,
  className,
}: Pick<ImageCarouselNavigationProps, 'hasNext' | 'onNext' | 'arrowIconSize' | 'className'>) => (
  <NextButton
    {...nextButtonStyles}
    onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      onNext();
    }}
    aria-hidden={true}
    disabled={hasNext}
    data-testid="carousel-next-button"
    className={className}
  >
    <ArrowIcon arrowSize={arrowIconSize ?? ICON_SIZE} alt="" />
  </NextButton>
);

export const ImageCarouselNavigation = ({
  hasNext,
  hasPrev,
  onPrevious,
  onNext,
  arrowIconSize,
}: ImageCarouselNavigationProps) => {
  return (
    <>
      <PrevControlButton hasPrev={hasPrev} onPrevious={onPrevious} arrowIconSize={arrowIconSize} />
      <NextControlButton hasNext={hasNext} onNext={onNext} arrowIconSize={arrowIconSize} />
    </>
  );
};
